import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import { message, Spin } from "antd";

import LdasApi from "../../../../apis/Ldas.api";
import { plutusS3 } from "../../../../utils/plutusS3";
import ContentHeaderComponent from "../../../layout/ContentHeaderComponent";
import LdasProductForm from "../LdasProductForm";
import useDeleteLdasProduct from "../useDeleteLdasProduct";

/** 제품 수정 폼 검증 */
const validateLdasProductEditForm = (values, serverImageKey) => {
  const brandName = typeof values.brand === "string" ? values.brand.trim() : "";
  const productName = typeof values.name === "string" ? values.name.trim() : "";
  if (!brandName) {
    return { ok: false, message: "브랜드를 입력해 주세요" };
  }
  if (!productName) {
    return { ok: false, message: "제품명을 입력해 주세요" };
  }
  if (!values.categoryTags || values.categoryTags.length === 0) {
    return { ok: false, message: "카테고리를 선택해 주세요" };
  }
  const hasNewFile = values.image instanceof File;
  const keptExisting =
    typeof values.image === "string" && values.image.trim() !== "";
  if (!hasNewFile && !keptExisting) {
    return {
      ok: false,
      message: "제품 이미지를 유지하거나 새로 선택해 주세요",
    };
  }
  if (!hasNewFile && keptExisting && !serverImageKey) {
    return { ok: false, message: "이미지 정보가 올바르지 않습니다" };
  }
  return { ok: true, brandName, productName };
};

const LdasProductEditComponent = () => {
  const history = useHistory();
  const { productId } = useParams();
  const [categories, setCategories] = useState([]);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const submitLockRef = useRef(false);
  const serverImageKeyRef = useRef("");
  const productIdRef = useRef(productId);
  productIdRef.current = productId;
  const { deleteLdasProductWithConfirm } = useDeleteLdasProduct();

  const fetchCategories = useCallback(async () => {
    try {
      const res = await LdasApi.readLdasCategoryList();
      // console.log("readLdasCategoryList res", res);
      if (res.code === 200) {
        setCategories(res.result.items);
      }
    } catch (error) {
      console.error("readLdasCategoryList", error);
    }
  }, []);

  const loadProductDetail = useCallback(async () => {
    const id = productId;
    if (!id) return;
    try {
      const res = await LdasApi.readLdasProductDetail(id);
      if (productIdRef.current !== id) return;
      if (res?.code !== 200) {
        message.error(res?.message ?? "제품을 불러올 수 없습니다");
        history.replace("/ldas/list");
        return;
      }
      setProduct(res.result);
      serverImageKeyRef.current =
        (res.result?.imageUrl && String(res.result.imageUrl).trim()) || "";
    } catch (error) {
      if (productIdRef.current !== id) return;
      console.error("readLdasProductDetail", error);
      message.error("제품을 불러올 수 없습니다");
      history.replace("/ldas/list");
    }
  }, [productId, history]);

  useEffect(() => {
    if (!productId) return undefined;

    let cancelled = false;
    setLoading(true);
    void Promise.all([fetchCategories(), loadProductDetail()]).finally(() => {
      if (!cancelled) {
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [productId]);

  const handleCancel = () => {
    history.push("/ldas/list");
  };

  const saveLdasProductImageForEdit = async (file, pid) => {
    const ext = file.type.split("/").pop() || "jpg";
    const key = `image/admin/ldas/product/${pid}.${ext}`;
    const res = await plutusS3
      .putObject({
        Bucket: process.env.REACT_APP_S3_BUCKETNAME,
        Key: key,
        Body: file,
        ContentType: file.type,
      })
      .promise();
    if (res.success !== true) {
      throw new Error("S3 업로드 실패");
    }
    return key;
  };

  const removePreviousLdasProductImageIfReplaced = async (
    previousKey,
    newKey,
  ) => {
    if (!previousKey || previousKey === newKey) return;
    try {
      await plutusS3.deleteObject({ Key: previousKey }).promise();
    } catch (error) {
      console.error("LdasProductEdit delete old image after update", error);
    }
  };

  const handleSubmit = async (values) => {
    if (submitLockRef.current) return;

    const validation = validateLdasProductEditForm(
      values,
      serverImageKeyRef.current,
    );
    if (!validation.ok) {
      message.error(validation.message);
      return;
    }

    submitLockRef.current = true;
    const hideLoading = message.loading("저장 중...", 0);

    try {
      const dtoBase = {
        brandName: validation.brandName,
        productName: validation.productName,
        description: (values.description ?? "").trim(),
        categoryIds: [Number(values.categoryTags[0].id)] ?? [],
      };

      if (values.image instanceof File) {
        const previousKey = serverImageKeyRef.current;
        const imageUrl = await saveLdasProductImageForEdit(
          values.image,
          productId,
        );
        const param = {
          ...dtoBase,
          imageUrl,
        };
        console.log("updateLdasProduct param", param);
        const res = await LdasApi.updateLdasProduct(productId, param);
        console.log("updateLdasProduct res", res);
        if (res.code === 200) {
          await removePreviousLdasProductImageIfReplaced(previousKey, imageUrl);
          message.success("제품이 수정되었습니다");
          history.push("/ldas/list");
          return;
        }
        message.error(res?.message ?? "제품 수정에 실패했습니다");
        try {
          await plutusS3.deleteObject({ Key: imageUrl }).promise();
        } catch (rollbackErr) {
          console.error("LdasProductEdit rollback new image", rollbackErr);
        }
        return;
      }

      const res = await LdasApi.updateLdasProduct(productId, {
        ...dtoBase,
        imageUrl: serverImageKeyRef.current,
      });
      if (res.code === 200) {
        message.success("제품이 수정되었습니다");
        history.push("/ldas/list");
        return;
      }
      message.error(res?.message ?? "제품 수정에 실패했습니다");
    } catch (error) {
      console.error("LdasProductEdit", error);
      message.error("제품 수정 중 오류가 발생했습니다");
    } finally {
      hideLoading();
      submitLockRef.current = false;
    }
  };

  const handleDelete = () => {
    void deleteLdasProductWithConfirm({
      productId,
      productName: product?.productName,
      imageKey: serverImageKeyRef.current || product?.imageUrl,
    });
  };

  const c0 = product?.categories?.[0];
  const initialValues =
    product === null || product === undefined
      ? null
      : {
          brand: product.brandName ?? "",
          name: product.productName ?? "",
          description: product.description ?? "",
          categoryTags: c0 ? [{ id: c0.id, name: c0.name }] : [],
          image: product.imageUrl
            ? `${(process.env.REACT_APP_S3_HOST || "").replace(/\/$/, "")}/${
                product.imageUrl
              }`
            : null,
        };

  const breadcrumb = [
    { label: "제품 관리", onClick: () => history.push("/ldas/list") },
    { label: "제품 수정" },
  ];

  if (loading || !initialValues) {
    return (
      <div className="ldas-page">
        <ContentHeaderComponent title="제품 수정" breadcrumb={breadcrumb} />
        <div className="ldas-page__content ldas-page__content--detail-loading">
          <Spin size="large" tip="불러오는 중..." />
        </div>
      </div>
    );
  }

  return (
    <div className="ldas-page">
      <ContentHeaderComponent title="제품 수정" breadcrumb={breadcrumb} />

      <div className="ldas-page__content">
        <LdasProductForm
          key={`ldas-product-edit-${productId}-${product?.id ?? "p"}`}
          mode="edit"
          initialValues={initialValues}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          onDelete={handleDelete}
          categories={categories}
          onCategoriesInvalidated={fetchCategories}
        />
      </div>
    </div>
  );
};

export default LdasProductEditComponent;
